import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  deleteDynamicAvailability,
  dynamicAvailabilityMapSelector,
  fetchDynamicAvailabilities,
  firstDynamicAvailabilitySelector,
  saveDynamicAvailability,
  secondDynamicAvailabilitySelector,
  setFirstDynamicAvailability as setFirstDynamicAvailabilityAction,
  setSecondDynamicAvailability as setSecondDynamicAvailabilityAction,
} from 'slices/dynamicAvailability.slice';
import { fetchFields } from 'slices/field.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import type { TDynamicAvailability } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import DynamicAvailability from './sections/DynamicAvailability';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const DynamicAvailabilityDrawer = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();
  const [isExpand, setIsExpand] = useState(true);

  const dynamicAvailabilitiesMap = useAppSelector(
    dynamicAvailabilityMapSelector
  );
  const firstDynamicAvailability = useAppSelector(
    firstDynamicAvailabilitySelector
  );
  const secondDynamicAvailability = useAppSelector(
    secondDynamicAvailabilitySelector
  );

  const firstDynamicAvailabilitySaveDisabled = useMemo(
    () =>
      !firstDynamicAvailability ||
      !dynamicAvailabilitiesMap[firstDynamicAvailability.id] ||
      checkSavedDisabled(
        firstDynamicAvailability,
        dynamicAvailabilitiesMap[firstDynamicAvailability.id]
      ),
    [firstDynamicAvailability, dynamicAvailabilitiesMap]
  );

  const isSecondDynamicAvailabilitySaveDisabled = useMemo(
    () =>
      !secondDynamicAvailability ||
      !dynamicAvailabilitiesMap[secondDynamicAvailability.id] ||
      checkSavedDisabled(
        secondDynamicAvailability,
        dynamicAvailabilitiesMap[secondDynamicAvailability.id]
      ),
    [secondDynamicAvailability, dynamicAvailabilitiesMap]
  );

  const setFirstResource = useCallback(
    (item: TDynamicAvailability) => setDynamicAvailability(true, item),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (item: TDynamicAvailability) => setDynamicAvailability(false, item),
    [dispatch]
  );

  useSelectResource(
    'integer',
    dynamicAvailabilitiesMap,
    firstDynamicAvailability,
    secondDynamicAvailability,
    setFirstResource,
    setSecondResource
  );

  useEffect(() => {
    dispatch(fetchDynamicAvailabilities());
    dispatch(fetchFields());
    dispatch(requestObjectTypes());
  }, []);

  const setDynamicAvailability = (
    isFirstDrawer: boolean,
    dynamicAvailability?: TDynamicAvailability,
    checkChanged = true
  ) => {
    if (checkChanged) {
      if (isFirstDrawer && !firstDynamicAvailabilitySaveDisabled) {
        return confirmDiscardPaddingChanges(true, () =>
          dispatch(setFirstDynamicAvailabilityAction(dynamicAvailability))
        );
      }
      if (!isFirstDrawer && !isSecondDynamicAvailabilitySaveDisabled) {
        return confirmDiscardPaddingChanges(false, () =>
          dispatch(setSecondDynamicAvailabilityAction(dynamicAvailability))
        );
      }
    }
    dispatch(
      isFirstDrawer
        ? setFirstDynamicAvailabilityAction(dynamicAvailability)
        : setSecondDynamicAvailabilityAction(dynamicAvailability)
    );
  };

  const onDelete = (dynamicAvailability: TDynamicAvailability) => {
    dispatch(deleteDynamicAvailability(dynamicAvailability.id));
  };

  const onSave = (
    isFirstDynamicAvailability: boolean,
    dynamicAvailability: TDynamicAvailability
  ) => {
    dispatch(
      saveDynamicAvailability({
        isFirstDynamicAvailability,
        dynamicAvailability,
      })
    );
  };

  const onClose = (isFirstDrawer: boolean) => {
    setDynamicAvailability(isFirstDrawer, undefined);
  };

  const onChange = (
    isFirstDrawer: boolean,
    updatedValues: Record<string, any>
  ) => {
    setDynamicAvailability(
      isFirstDrawer,
      {
        ...(isFirstDrawer
          ? firstDynamicAvailability!
          : secondDynamicAvailability!),
        ...updatedValues,
      },
      false
    );
  };

  const onDiscard = (
    isFirstDrawer: boolean,
    dynamicAvailability: TDynamicAvailability
  ) => {
    const originalPaddingRule =
      dynamicAvailabilitiesMap[dynamicAvailability.id];
    setDynamicAvailability(isFirstDrawer, originalPaddingRule, false);
  };

  return (
    <>
      <TEDrawer
        expandWidth="70vw"
        firstDrawer={{
          title: firstDynamicAvailability?.name || '',
          content: (
            <DynamicAvailability
              dynamicAvailability={firstDynamicAvailability}
              onChange={(updatedValues: Record<string, any>) =>
                onChange(true, updatedValues)
              }
              isExpand={isExpand}
            />
          ),
          drawerOpen: !!firstDynamicAvailability,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText(
                'dynamic_availability_confirmation_delete'
              )}
              handleDelete={() => onDelete(firstDynamicAvailability!)}
              handleDiscard={() => onDiscard(true, firstDynamicAvailability!)}
              handleSave={() => onSave(true, firstDynamicAvailability!)}
              isSaveDisable={firstDynamicAvailabilitySaveDisabled}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(true),
        }}
        secondDrawer={{
          title: secondDynamicAvailability?.name || '',
          content: (
            <DynamicAvailability
              dynamicAvailability={secondDynamicAvailability}
              onChange={(updatedValues: Record<string, any>) =>
                onChange(false, updatedValues)
              }
              isExpand={isExpand}
            />
          ),
          drawerOpen: !!secondDynamicAvailability,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText(
                'dynamic_availability_confirmation_delete'
              )}
              handleDelete={() => onDelete(secondDynamicAvailability!)}
              handleDiscard={() => onDiscard(false, secondDynamicAvailability!)}
              handleSave={() => onSave(false, secondDynamicAvailability!)}
              isSaveDisable={isSecondDynamicAvailabilitySaveDisabled}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(false),
        }}
        onExpand={setIsExpand}
        defaultExpand={isExpand}
      />
    </>
  );
};

export default memo(DynamicAvailabilityDrawer);
